import { defineStore } from "pinia";
import { useWindowSize } from "@vueuse/core";
import { simpleCrypt, simpleDecrypt } from "@/assets/js/helpers";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { type Table } from "@/models/table";
import { type Note } from "@/models/note";
import { type Layer } from "@/models/layer";
import { type ImageObject } from "@/models/imageObject";
dayjs.extend(utc);
dayjs.extend(timezone);

const APP_NAME = import.meta.env.VITE_APP_APP_NAME;

function formatDatepickerDate(date: string): string {
	const localDate = dayjs(date);
	const utcDate = localDate.utc();
	return utcDate.format("YYYY-MM-DD HH:mm:ss");
}

function utcToLocale(date: string, withTime: boolean): string {
	const res = dayjs.utc(date).local(); // temp solution !!!!!!!
	// console.log(res.format("YYYY-MM-DD HH:mm:ss"));

	return withTime
		? res.format("MMM DD, YYYY HH:mm")
		: res.format("MMM DD, YYYY");
}

function getFirstLetter(str: string): string {
	if (typeof str !== "string" || str.trim() === "") {
		return "";
	}
	return str.trim().charAt(0);
}

export const useDefaultStore = defineStore("main", () => {
	const { width } = useWindowSize();
	const isMobileToggled = ref(false);

	const cursorPosition = ref<{ top: number; left: number } | null>(null);

	// Modes
	const validModes = ref({
		SELECT: "select-item",
		NEW_TABLE: "place-a-new-table-object",
		NEW_NOTE: "place-a-new-note-object",
		NEW_LAYER: "place-a-new-layer-object",
		NEW_IMAGE_OBJECT: "place-a-new-image-object",
		DRAG: "scroll-on-drag",
		DELETE: "delete-item",
	});
	const currentMode = ref(validModes.value.SELECT);
	const isGridBg = ref(true);

	const validTypes = ref({
		TABLE: "Table",
		NOTE: "Note",
		LAYER: "Layer",
		IMAGE_OBJECT: "Image",
	});

	const sidebarWidth = ref(40);
	const zoomLevel = ref(1);
	const actionBarBool = ref(false);
	const activeItemChangeBool = ref(false);
	const objects = ref<(Table | Note | Layer | ImageObject)[]>([]);
	const objectToCopy = ref<(Table | Note | Layer | ImageObject) | null>(null);
	const isTableResizing = ref(false);

	const mailRegex =
		// eslint-disable-next-line unicorn/better-regex
		/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
	const passwordRegex =
		/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

	const isMobileSize = computed(() => width.value < 768);
	const isMobileView = computed(
		() => isMobileSize.value && isMobileToggled.value,
	);
	const isVisibleSideBar = ref(!isMobileSize.value);

	watch(isMobileSize, (val) => {
		if (!val) {
			isMobileToggled.value = false;
		}
	});

	function decodePath(name: string) {
		return simpleDecrypt("abc", name);
	}
	function encodePath(name: string) {
		return simpleCrypt("abc", name);
	}
	/*async function onClickLoginPage() {
		// Used to return user to the previous page
		const currentPageName = String(route.name);

		// Exceptions - Auth pages
		let ppn: string | undefined = encodePath(currentPageName);
		if (route.matched.some((rt) => rt.name === "AuthPage")) {
			ppn = undefined;
		}

		// await router.push({
		// 	name: "LoginPage",
		// 	query: {
		// 		ppn,
		// 	},
		// });
	}*/

	function smoothScrollIntoView(id: string) {
		const el = document.getElementById(id);

		if (el) {
			el.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	}

	// Clear active object

	function activeElementClear() {
		const activeElement: HTMLElement = document.querySelector(".item-active");
		if (activeElement) {
			const handlers = activeElement.querySelectorAll(".resize-handler");
			handlers.forEach((handler) => handler.remove());
			activeElement.classList.remove("item-active");
		}
	}

	return {
		isMobileToggled,
		isMobileView,
		decodePath,
		encodePath,
		APP_NAME,
		formatDatepickerDate,
		width,
		smoothScrollIntoView,
		isMobileSize,
		mailRegex,
		passwordRegex,
		isVisibleSideBar,
		utcToLocale,
		getFirstLetter,
		cursorPosition,
		currentMode,
		validModes,
		validTypes,
		sidebarWidth,
		zoomLevel,
		actionBarBool,
		activeItemChangeBool,
		objects,
		isGridBg,
		activeElementClear,
		objectToCopy,
		isTableResizing,
	};
});
